/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useWheel } from './useWheel';
import { getGestureZoomFactor, zoomTime } from '../utils/timeZoomUtils';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { OnTimeComponentViewportChange, TimeComponentZoomParameters } from '../timeComponent.types';
import { getPositionTime } from '../utils/timeUtils';

type UseWheelZoomOptions = {
  width: number;
  startTime: number;
  endTime: number;
  viewportStartTime: number;
  viewportEndTime: number;
  majorAxisZoomOrder?: TimeComponentZoomParameters['majorAxisZoomOrder'];
  majorAxisZoomOrderIndex?: TimeComponentZoomParameters['majorAxisZoomOrderIndex'];
  minorAxisZoomOrder: TimeComponentZoomParameters['minorAxisZoomOrder'];
  minorAxisZoomOrderIndex: TimeComponentZoomParameters['minorAxisZoomOrderIndex'];
  zoomOrderViewDurationThresholds: ReturnType<
    TimeComponentZoomParameters['getZoomOrderViewDurationThresholds']
  >;
  minZoomViewportDuration: TimeComponentZoomParameters['minZoomViewportDuration'];
  maxZoomViewportDuration: TimeComponentZoomParameters['maxZoomViewportDuration'];
  onViewportChange?: OnTimeComponentViewportChange;
  isDisabled?: boolean;
};

/**
 * Hook that enables and handles wheen zoom gestures.
 */
const useWheelZoom = ({
  width,
  startTime,
  endTime,
  viewportStartTime,
  viewportEndTime,
  majorAxisZoomOrder,
  majorAxisZoomOrderIndex,
  minorAxisZoomOrder,
  minorAxisZoomOrderIndex,
  zoomOrderViewDurationThresholds,
  minZoomViewportDuration,
  maxZoomViewportDuration,
  onViewportChange,
  isDisabled = false
}: UseWheelZoomOptions) => {
  const { direction } = useUser();
  const isRTL = direction === 'rtl';

  const wheelProps = useWheel({
    onWheel: ({ x, deltaY, ctrlKey }) => {
      // WheelEvent with ctrlKey means pinch/zoom gesture
      if (!ctrlKey) return;

      const time = getPositionTime(x, viewportStartTime, viewportEndTime, width, isRTL);
      zoomTime(
        time,
        viewportStartTime,
        viewportEndTime,
        startTime,
        endTime,
        getGestureZoomFactor(deltaY),
        minZoomViewportDuration,
        maxZoomViewportDuration,
        zoomOrderViewDurationThresholds,
        minorAxisZoomOrder,
        minorAxisZoomOrderIndex,
        majorAxisZoomOrder,
        majorAxisZoomOrderIndex,
        onViewportChange
      );
    },
    isDisabled
  });

  return isDisabled ? {} : wheelProps;
};

export { useWheelZoom };
